import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand, ModeBadge } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { LayoutDashboard, Crosshair, KanbanSquare, Settings, LogOut, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/opportunities", label: "Find Clients", icon: Crosshair },
  { to: "/app/pipeline", label: "Pipeline", icon: KanbanSquare },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null);
  useEffect(() => { api.get("/mode").then(({ data }) => setMode(data)).catch(() => setMode({ mode: "demo" })); }, []);

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center px-5 border-b border-slate-800"><Brand /></div>
      <nav className="flex-1 p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-primary/10 text-primary border border-primary/20" : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"}`}>
            <n.icon className="w-4 h-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="px-3 py-2 mb-1">
          <p className="text-xs font-medium text-slate-200 truncate">{user?.name}</p>
          <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
        </div>
        <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800" data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}>
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-slate-800 fixed inset-y-0"><SidebarInner /></aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-background border-r border-slate-800"><SidebarInner /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-60 min-w-0">
        <div className="sticky top-0 z-30 h-14 border-b border-slate-800 bg-background/85 backdrop-blur-xl flex items-center justify-between px-4 lg:px-6">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="w-1.5 h-1.5 rounded-full bg-primary lf-live-dot" /> {mode?.mode === "live" ? "Live data connected" : "Intelligence engine online"}
          </div>
          <div className="flex items-center gap-2" data-testid="header-mode">
            {mode && <ModeBadge mode={mode.mode} testid="header-mode-badge" />}
          </div>
        </div>
        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full min-w-0 overflow-x-hidden"><Outlet /></main>
      </div>
    </div>
  );
}
